import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { lighten, makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { Skeleton } from '@material-ui/lab'
import useSnackbar from '../hooks/useSnackar'

const loadingUsers = [
  {
    id: 1,
    company: <Skeleton />,
    email: <Skeleton />,
    city: { name: <Skeleton /> },
    role: <Skeleton />,
    locked: false,
    clientVegaCE: false,
    TVs: [],
  },
  {
    id: 2,
    company: <Skeleton />,
    email: <Skeleton />,
    city: { name: <Skeleton /> },
    role: <Skeleton />,
    locked: false,
    clientVegaCE: false,
    TVs: [],
  },
  {
    id: 3,
    company: <Skeleton />,
    email: <Skeleton />,
    city: { name: <Skeleton /> },
    role: <Skeleton />,
    locked: false,
    clientVegaCE: false,
    TVs: [],
  },
  {
    id: 4,
    company: <Skeleton />,
    email: <Skeleton />,
    city: { name: <Skeleton /> },
    role: <Skeleton />,
    locked: false,
    clientVegaCE: false,
    TVs: [],
  },
  {
    id: 5,
    company: <Skeleton />,
    email: <Skeleton />,
    city: { name: <Skeleton /> },
    role: <Skeleton />,
    locked: false,
    clientVegaCE: false,
    TVs: [],
  },
]

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const headCells = [
  { id: 'id', numeric: false, disablePadding: true, label: 'ID' },
  { id: 'company', numeric: false, disablePadding: false, label: 'Entreprise' },
  { id: 'email', numeric: false, disablePadding: false, label: 'Email' },
  { id: 'role', numeric: false, disablePadding: false, label: 'Rôle' },
  { id: 'city', numeric: false, disablePadding: false, label: 'Localisation' },
  { id: 'TVs', numeric: false, disablePadding: false, label: 'TVs' },
  { id: 'clientVegaCE', numeric: false, disablePadding: false, label: 'Client VegaCE' },
  { id: 'locked', numeric: false, disablePadding: false, label: 'Bloqué' },
]

function EnhancedTableHead(props) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
}

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  title: {
    flex: '1 1 100%',
  },
}))

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles()
  const { numSelected, handleDelete } = props

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
          {numSelected} ligne(s) sélectionné(s)
        </Typography>
      ) : (
          <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
            Utilisateurs
          </Typography>
        )}

      {numSelected > 0 && (
        <Tooltip title="Delete">
          <IconButton aria-label="delete" onClick={handleDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  )
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}))

function Row(props) {
  const { row, index, handleSelectClick, handleRowClick, isSelected, toggleLockUser, toggleClientVegaCEUser } = props
  const isItemSelected = isSelected(row.id)
  const labelId = `enhanced-table-checkbox-${index}`
  return (
    <TableRow
      hover
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
    >
      <TableCell padding="checkbox" onClick={(event) => handleSelectClick(event, row.id)}>
        <Checkbox
          checked={isItemSelected}
          inputProps={{ 'aria-labelledby': labelId }}
        />
      </TableCell>
      <TableCell component="th" id={labelId} scope="row" padding="none" onClick={() => handleRowClick(row)}>
        {row.id}
      </TableCell>
      <TableCell align="left" onClick={() => handleRowClick(row)}>{row.company}</TableCell>
      <TableCell align="left" onClick={() => handleRowClick(row)}>{row.email}</TableCell>
      <TableCell align="left" onClick={() => handleRowClick(row)}>{row.role}</TableCell>
      <TableCell align="left" onClick={() => handleRowClick(row)}>{row.city?.name || '-'}</TableCell>
      <TableCell align="left" onClick={() => handleRowClick(row)}>{row.TVs?.map(({ name }) => name).join(', ') || '-'}</TableCell>
      <TableCell align="left">
        <FormControlLabel
          control={<Switch checked={row.clientVegaCE} color="primary" onChange={() => toggleClientVegaCEUser({ variables: { id: parseInt(row.id) } })} />}
        // label={row.clientVegaCE ? 'Oui' : 'Non'}
        />
      </TableCell>
      <TableCell align="left">
        <FormControlLabel
          control={<Switch checked={row.locked} color="primary" onChange={() => toggleLockUser({ variables: { id: parseInt(row.id) } })} />}
        // label={row.locked ? 'Oui' : 'Non'}
        />
      </TableCell>
    </TableRow>
  )
}

export default function UserTable({ rows, deleteUsers, toggleClientVegaCEUser, toggleLockUser, loading, handleRowClick }) {
  const classes = useStyles()
  const { addSuccessSnackbar } = useSnackbar()
  const [order, setOrder] = React.useState('asc')
  const [orderBy, setOrderBy] = React.useState('id')
  const [selected, setSelected] = React.useState([])
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    setSelected(newSelected)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const isSelected = (id) => selected.indexOf(id) !== -1

  const emptyRows = rows.length < rowsPerPage ? 0 : rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage)

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar numSelected={selected.length} handleDelete={() => {
          deleteUsers({ variables: { ids: selected.map(item => parseInt(item)) } }).then(({ data }) => {
            if (data?.deleteUsers > 0) {
              addSuccessSnackbar('Supprimé avec succès')
              setSelected([])
            }
          })
        }} />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(loading ? loadingUsers : rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .length ?
                stableSort(loading ? loadingUsers : rows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return (
                      <Row
                        key={row.id}
                        toggleLockUser={toggleLockUser}
                        toggleClientVegaCEUser={toggleClientVegaCEUser}
                        isSelected={isSelected}
                        handleSelectClick={handleClick}
                        row={row}
                        index={index}
                        handleRowClick={handleRowClick}
                      />
                    )
                  }) : (
                  <TableRow>
                    <TableCell align="center" colSpan={8}>Aucune donnée</TableCell>
                  </TableRow>
                )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={8} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 75]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
          labelRowsPerPage={'Lignes par page :'}
        />
      </Paper>
    </div>
  )
}
